import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { Subject, type CourseDigital } from '@chancenwerk/db/courses/CourseDigital.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @customElement chancen-component-choose-digital-courses
 *
 * Shows boxes to choose the IntensiveCourses for a Student based on some possible courses.
 *
 * **Possible attributes:**
 *
 * - `digitalCourses: CourseDigital[]` - The courses to choose from
 * - `digitalCoursesChoosen: CourseDigital[]` - The choosen courses
 * - `student: Student` - The student to mutate
 * - `horizontal: Boolean` - Switch to horizontal wrapping list
 *
 * **Possible events:**
 *
 * - `value-picked: ValueEvent<CourseDigital[]>` - Emitted when the Student was changed
 */
@customElement('chancen-component-choose-digital-courses')
export class ChancenComponentChooseDigitalCourses extends ChemistryLitElement {
  @property({ attribute: false }) student?: Student;
  @property({ attribute: false }) digitalCourses: CourseDigital[] = [];
  @property({ attribute: false }) digitalCoursesChoosen: CourseDigital[] = [];
  @property({ type: Boolean }) horizontal = false;

  private _emitChoosenChanged() {
    const event = new CustomEvent('value-picked', {
      detail: {
        value: this.digitalCoursesChoosen,
      },
    });
    this.dispatchEvent(event);
  }

  private _onChoiceClick(c: CourseDigital) {
    const index = this.digitalCoursesChoosen.findIndex(d => d.weekday === c.weekday && d.startTime === c.startTime);

    if (index === undefined || index === -1) this.digitalCoursesChoosen.push(c);
    else this.digitalCoursesChoosen.splice(index, 1);

    this._emitChoosenChanged();
    this.requestUpdate();
  }

  private _choiceItem(choosen: boolean, c: CourseDigital) {
    return html`
      <cr-basic-card
        ${css({
          boxShadow: 'none',
          cursor: 'pointer',
          background: choosen ? this.colors.primary.shade(100) : this.colors.neutral.shade(100),
          minWidth: '400px',
        })}
        @click=${() => this._onChoiceClick(c)}
      >
        <cr-flex horizontal space="md" crossaxis-center>
          <cr-icon
            ${css({
              color: choosen ? this.colors.primary : this.colors.neutral,
              fontSize: '1.8rem',
            })}
          >
            ${choosen ? 'done' : 'close'}
          </cr-icon>
          <cr-flex ${css({ minWidth: '250px' })} space="sm" horizontal crossaxis-center>
            <cr-flex space="sm">
              <cr-text bold> ${c.subject} </cr-text>
              <cr-flex space="sm" horizontal>
                <cr-text> ${c.weekday} </cr-text>
                <cr-text><nobr>${c.startTime?.toFormat('HH:mm')} - ${c.endTime?.toFormat('HH:mm')}</nobr></cr-text>
                <cr-text>
                  ${c.subject === Subject.NATURWISSENSCHAFTEN ? '(Kurs wird pro Jgst. gebildet)' : ''}
                  ${c.jahrgangsstufe ? `(Jgst. ${c.jahrgangsstufe})` : ''}
                </cr-text>
              </cr-flex>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-basic-card>
    `;
  }

  override render() {
    return html` <cr-flex space="md" ?horizontal=${this.horizontal} ?wrap=${this.horizontal}>
      ${(this.digitalCourses ?? []).length === 0
        ? html`<cr-basic-card ${css({ boxShadow: 'none', background: this.colors.tone.error.shade(100) })}>
            <cr-text>Leider stehen aktuell keine Termine zur Verfügung</cr-text>
          </cr-basic-card>`
        : nothing}
      ${repeat(
        (this.digitalCourses ?? []).sort((a, b) => {
          return (a.subject ?? '') < (b.subject ?? '') ? -1 : (a.subject ?? '') > (b.subject ?? '') ? 1 : 0;
        }),
        c => c._id,
        c => {
          const choosen = this.digitalCoursesChoosen.some(d => d.weekday === c.weekday && d.startTime === c.startTime);
          return this._choiceItem(!!choosen, c);
        },
      )}
    </cr-flex>`;
  }
}
